import { useEffect } from 'react'
import { hasKey, isObject } from '../../../../../React/Common/Utils'
import { IsVarEmpty } from '../../../../../NonReact/utils/GlobalFunctions'

interface ExternalComponent {
    mount: (container: HTMLElement, options: Record<string, unknown>) => void
}

/**
 * Recursively attempts to mount the external component by checking for its existence on the window object.
 *
 * @param container - The HTML element that serves as the container for the external component.
 * @param componentID - The unique identifier for the external component, used to access it from the window object.
 * @param mountOptions - An object containing options that will be passed to the component's mount function when it is called.
 *
 * This function uses requestAnimationFrame to continuously check for the presence of the external component on the window object.
 * Once the component is found and verified to have a mount function, it calls the mount function with the provided container and mount options.
 * This approach ensures that the external component is mounted as soon as it becomes available, without blocking the main thread or causing unnecessary delays.
 */
const mountComponent = (container: HTMLElement, componentID: string, mountOptions: Record<string, unknown>): void => {
    const component: unknown = (window as unknown as { [key: string]: unknown })[componentID]

    if (isObject<ExternalComponent>(component) && hasKey(component, 'mount')) {
        component.mount(container, mountOptions)
    } else {
        requestAnimationFrame((): void => mountComponent(container, componentID, mountOptions))
    }
}

/**
 * Custom React hook to mount an external component onto a specified container element once the component becomes available on the window object.
 *
 * @param componentID - The unique identifier for the external component, used to access it from the window object and to identify the container element in the DOM.
 * @param mountOptions - An object containing options that will be passed to the component's mount function when it is called.
 */
export const useMountExternalComponent = (componentID: string, mountOptions: Record<string, unknown>): void => {
    useEffect((): void => {
        const container: Nullable<HTMLElement> = document.getElementById(componentID)

        if (IsVarEmpty(container)) return

        mountComponent(container, componentID, mountOptions)
    }, [componentID, mountOptions])
}
